class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.TARGET_ITEMS = Number.parseInt(this.dataset.targetItems, 10) || 10;

    this.cachedResults                 = {};
    this.predictiveSearchResults       = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances  = document.querySelectorAll('predictive-search');
    this.isOpen                        = false;
    this.abortController               = new AbortController();
    this.searchTerm                    = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    }
    else if (this.getAttribute('results') === 'true') {
      this.open();
    }
    else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    }
    else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  async getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();

    try {
      const [predictiveMarkup, productItems] = await Promise.all([
        this.fetchPredictiveMarkup(searchTerm),
        this.fetchFilteredProducts(searchTerm)
      ]);
      const resultsMarkup = this.mergeProductResults(predictiveMarkup, productItems);

      if (searchTerm !== this.searchTerm) return;

      this.allPredictiveSearchInstances.forEach((instance) => {
        instance.cachedResults[queryKey] = resultsMarkup;
      });
      this.renderSearchResults(resultsMarkup);
    }
    catch (error) {
      if (error?.name === 'AbortError' || error?.code === 20) return;
      this.close();
      throw error;
    }
  }

  async fetchPredictiveMarkup(searchTerm) {
    const params = new URLSearchParams({
      q: searchTerm,
      section_id: 'predictive-search'
    });
    const response = await fetch(`${routes.predictive_search_url}?${params}`, {
      signal: this.abortController.signal
    });

    if (!response.ok) throw new Error(`Predictive search failed with HTTP ${response.status}`);

    const document = new DOMParser().parseFromString(await response.text(), 'text/html');
    const section = document.querySelector('#shopify-section-predictive-search');

    if (!section) throw new Error('Predictive search section is missing from the response');

    return section.innerHTML;
  }

  async fetchFilteredProducts(searchTerm) {
    const productItems = [];
    const visitedPages = new Set();
    let page = 1;

    while (productItems.length < this.TARGET_ITEMS && !visitedPages.has(page)) {
      visitedPages.add(page);
      const params = new URLSearchParams({
        q: searchTerm,
        type: 'product',
        'options[prefix]': 'last',
        'options[unavailable_products]': 'last',
        'filter.p.m.custom.display_product': 'true',
        section_id: 'filtered-predictive-search-products',
        page: String(page)
      });
      const response = await fetch(`${this.input.form.action}?${params}`, {
        signal: this.abortController.signal
      });

      if (!response.ok) throw new Error(`Filtered product search failed with HTTP ${response.status}`);

      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const pageElement = document.querySelector('[data-filtered-predictive-search-products]');

      if (!pageElement) throw new Error('Filtered product search section is missing from the response');

      const pageItems = pageElement.querySelectorAll('[data-predictive-search-product]');
      for (const item of pageItems) {
        if (productItems.length >= this.TARGET_ITEMS) break;
        productItems.push(item);
      }

      const nextPage = Number.parseInt(pageElement.dataset.nextPage, 10);
      if (!nextPage) break;
      page = nextPage;
    }

    return productItems;
  }

  mergeProductResults(predictiveMarkup, productItems) {
    const document = new DOMParser().parseFromString(predictiveMarkup, 'text/html');
    const results = document.querySelector('#predictive-search-results');
    if (!results) return predictiveMarkup;

    let list = results.querySelector('#predictive-search-results-products-list');
    if (productItems.length > 0 && !list) list = this.createProductResultsList(document, results);

    if (list) {
      const productContainer = list.parentElement;

      if (productItems.length === 0) {
        const productGroup = productContainer.parentElement;
        productContainer.remove();
        if (!productGroup.children.length) productGroup.remove();
      }
      else {
        list.replaceChildren();
        productItems.forEach((item, index) => {
          const importedItem = document.importNode(item, true);
          importedItem.id = `predictive-search-option-product-${index + 1}`;
          list.appendChild(importedItem);
        });
      }
    }

    const groupsWrapper = results.querySelector('#predictive-search-results-groups-wrapper');
    groupsWrapper?.classList.toggle('predictive-search__results-groups-wrapper--no-products', productItems.length === 0);

    const totalResults = results.querySelectorAll('.predictive-search__list-item').length;
    const liveRegionCount = document.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegionCount) liveRegionCount.textContent = this.formatResultsCount(totalResults);

    const headingCount = results.querySelector('#predictive-search-products .visually-hidden');
    if (headingCount) headingCount.textContent = this.formatResultsCount(productItems.length);

    return document.body.innerHTML;
  }

  createProductResultsList(document, results) {
    let groupsWrapper = results.querySelector('#predictive-search-results-groups-wrapper');
    if (!groupsWrapper) {
      groupsWrapper = document.createElement('div');
      groupsWrapper.id = 'predictive-search-results-groups-wrapper';
      groupsWrapper.className = 'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions';
      results.prepend(groupsWrapper);
    }

    const existingGroups = Array.from(groupsWrapper.children)
      .filter((element) => element.classList.contains('predictive-search__result-group'));
    const group = existingGroups.reverse().find((element) => element.querySelector('.predictive-search__pages-wrapper'))
      || document.createElement('div');
    if (!group.classList.contains('predictive-search__result-group')) {
      group.className = 'predictive-search__result-group';
    }

    const container = document.createElement('div');
    const heading = document.createElement('h2');
    heading.id = 'predictive-search-products';
    heading.className = 'predictive-search__heading text-body caption-with-letter-spacing';
    const count = document.createElement('span');
    count.className = 'visually-hidden';
    heading.append(document.createTextNode(`${this.dataset.productsLabel || 'Products'} `), count);

    const list = document.createElement('ul');
    list.id = 'predictive-search-results-products-list';
    list.className = 'predictive-search__results-list list-unstyled';
    list.setAttribute('aria-labelledby', heading.id);
    list.setAttribute('role', 'group');

    container.append(heading, list);
    group.prepend(container);
    if (!group.parentElement) groupsWrapper.appendChild(group);

    return list;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }

  formatResultsCount(count) {
    // Read localized templates from the dataset.
    const one   = this.dataset.resultsOne   || '{{ count }} result',
      other = this.dataset.resultsOther || '{{ count }} results';

    // Select the plural form for the current language.
    const lang = document.documentElement.lang || 'en',
      pr   = new Intl.PluralRules(lang),
      cat  = pr.select(count);

    // Fall back to the generic plural template.
    const tpl = (cat === 'one') ? one : other;

    return tpl.replace(/\{\{\s*count\s*\}\}/g, String(count));
  }
}

customElements.define('predictive-search', PredictiveSearch);
